import { initializeApp } from 'firebase/app'
import { getAuth, browserLocalPersistence, setPersistence } from 'firebase/auth'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager
} from 'firebase/firestore'

function trimEnvValue(value: unknown): string {
  return String(value ?? '').trim()
}

function requireFirebaseWebConfig() {
  const apiKey = trimEnvValue(import.meta.env.VITE_FIREBASE_API_KEY)
  const authDomain = trimEnvValue(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN)
  const projectId = trimEnvValue(import.meta.env.VITE_FIREBASE_PROJECT_ID)
  const storageBucket = trimEnvValue(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET)
  const messagingSenderId = trimEnvValue(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID)
  const appId = trimEnvValue(import.meta.env.VITE_FIREBASE_APP_ID)
  const missing = [
    !apiKey && 'VITE_FIREBASE_API_KEY',
    !authDomain && 'VITE_FIREBASE_AUTH_DOMAIN',
    !projectId && 'VITE_FIREBASE_PROJECT_ID',
    !storageBucket && 'VITE_FIREBASE_STORAGE_BUCKET',
    !messagingSenderId && 'VITE_FIREBASE_MESSAGING_SENDER_ID',
    !appId && 'VITE_FIREBASE_APP_ID'
  ].filter(Boolean)
  if (missing.length > 0) {
    throw new Error(
      `Firebase is not configured (${missing.join(', ')} missing). Copy web/.env.example to web/.env.local, ` +
        'add your Firebase Web app config, restart `npm run web:dev`.'
    )
  }
  return { apiKey, authDomain, projectId, storageBucket, messagingSenderId, appId }
}

const firebaseConfig = requireFirebaseWebConfig()

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentSingleTabManager(undefined)
  })
})

const firestoreEmulatorHost = String(import.meta.env.VITE_FIRESTORE_EMULATOR_HOST ?? '').trim()
if (firestoreEmulatorHost) {
  const [host, portText] = firestoreEmulatorHost.split(':')
  const port = Number(portText || 8080)
  if (host && Number.isFinite(port)) {
    connectFirestoreEmulator(db, host, port)
  }
}

setPersistence(auth, browserLocalPersistence).catch(() => {})

// Initialize Firebase App Check (reCAPTCHA v3). Required for the staged
// rollout that flips Firestore + Functions App Check enforcement on in
// the Firebase Console (~7 days after web + desktop clients ship verified
// tokens). See docs/runbooks/firestore-app-check-mfa-rollout.md.
const appCheckSiteKey = String(import.meta.env.VITE_FIREBASE_APP_CHECK_SITE_KEY ?? '').trim()
const appCheckDebugToken = String(import.meta.env.VITE_FIREBASE_APP_CHECK_DEBUG_TOKEN ?? '').trim()
if (appCheckSiteKey) {
  if (appCheckDebugToken) {
    // The Firebase App Check SDK reads this global at init time.
    Object.defineProperty(globalThis, 'FIREBASE_APPCHECK_DEBUG_TOKEN', {
      configurable: true,
      writable: true,
      value: appCheckDebugToken.toLowerCase() === 'true' ? true : appCheckDebugToken
    })
  }
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true
    })
  } catch {
    // App Check init is best-effort. While enforcement is unset in the
    // Console, missing tokens are tolerated; rules still gate on auth.
  }
}
